// Khung app và các màn.
//
// Trước đây có ~30 class CSS tự định nghĩa (.screen, .topbar, .mistext,
// .row-between, .stack, .label, .eyebrow, .err, .banner…). Giờ dùng thẳng
// utility của Tailwind.
//
// Nơi lặp lại nhiều lần và mang NGHĨA thì mới tách thành biến hằng ở
// đây — vd cùng một kiểu "tiêu đề + phần bên phải" xuất hiện ở bốn màn,
// gõ lại bốn lần thì lần thứ tư sẽ lệch.

import type { ReactNode } from 'react'

/** Khung app. Điện thoại chiếm hết; desktop giới hạn để chữ không giãn
 *  thành dải quá rộng — trên 60 ký tự mắt khó quét dòng. */
export function Shell({ children, tab }: { children: ReactNode; tab?: ReactNode }) {
  return (
    <div className="flex min-h-dvh w-full max-w-lg flex-col bg-surface md:border-x md:border-ink/10">
      {children}
      {tab}
    </div>
  )
}

/** Vùng nội dung cuộn được. */
export function Screen({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={
        'flex flex-1 flex-col overflow-y-auto px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-4 ' +
        className
      }
    >
      {children}
    </div>
  )
}

/** Chữ nhỏ mờ, dùng cho ghi chú và mô tả. Không dùng cho nhãn quan trọng. */
export function Note({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <p className={`text-sm leading-relaxed text-ink-faint ${className}`}>{children}</p>
}

/** Nhãn nhóm: "MỤC TIÊU HÔM NAY", "CẦN XEM LẠI". */
export function Label({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`text-xs font-semibold tracking-wide text-ink-faint uppercase ${className}`}>
      {children}
    </div>
  )
}

/** Nhãn nhỏ phía trên tiêu đề màn. */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="font-mono text-xs font-semibold tracking-widest text-accent uppercase">
      {children}
    </div>
  )
}

/** Hàng hai đầu: trái + phải giãn hết chiều rộng. Dùng rất nhiều. */
export function Between({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return <div className={`flex items-center justify-between gap-3 ${className}`}>{children}</div>
}

/** Ô báo lỗi / báo thành công. */
export function Banner({
  children,
  tone = 'bad',
}: {
  children: ReactNode
  tone?: 'bad' | 'good' | 'warn'
}) {
  const cls =
    tone === 'good'
      ? 'border-good bg-good-soft text-good'
      : tone === 'warn'
        ? 'border-gold bg-gold-soft text-ink'
        : 'border-bad bg-bad-soft text-bad'
  return (
    <div className={`mb-3 rounded-xl border px-3 py-2.5 text-sm leading-relaxed ${cls}`} role="alert">
      {children}
    </div>
  )
}

/** Vùng bấm kiểu thẻ — dùng cho từng đề mục trong màn chọn. */
export function Tile({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`card w-full cursor-pointer text-left transition-colors ${
        disabled ? 'cursor-default opacity-60' : 'hover:border-accent/40'
      }`}
    >
      {children}
    </button>
  )
}

